import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../../../ui/card';
import {
  Activity,
  UserPlus,
  FileText,
  Clock,
  AlertCircle,
  CreditCard,
  MessageSquare
} from 'lucide-react';

const HealthMetric = ({ icon: Icon, label, value, status }) => {
  const statusColors = {
    good: 'text-emerald-600',
    warning: 'text-amber-500',
    critical: 'text-red-500',
    neutral: 'text-gray-600'
  };

  const dotColors = {
    good: 'bg-emerald-500',
    warning: 'bg-amber-500',
    critical: 'bg-red-500',
    neutral: 'bg-gray-400'
  };

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-stone-100 last:border-0">
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${statusColors[status]}`}>
          {value}
        </span>
        {status !== 'neutral' && (
          <span className={`w-2 h-2 rounded-full ${dotColors[status]} ${status === 'critical' ? 'animate-pulse' : ''}`} />
        )}
      </div>
    </div>
  );
};

export function SystemHealthPanel({ health, loading }) {
  if (loading || !health) {
    return (
      <Card className="p-5 bg-white border-stone-200/80">
        <div className="h-[250px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Card>
    );
  }

  const getStatus = (value, thresholds) => {
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.warning) return 'warning';
    if (value > 0) return 'good';
    return 'neutral';
  };

  const metrics = [
    {
      icon: UserPlus,
      label: 'New Registrations Today',
      value: `+${health.registrationsToday}`,
      status: health.registrationsToday > 0 ? 'good' : 'neutral'
    },
    {
      icon: FileText,
      label: 'Applications Today',
      value: `+${health.applicationsToday}`,
      status: health.applicationsToday > 0 ? 'good' : 'neutral'
    },
    {
      icon: Clock,
      label: 'Pending Service Requests',
      value: health.pendingRequests,
      status: getStatus(health.pendingRequests, { warning: 10, critical: 25 })
    },
    {
      icon: AlertCircle,
      label: 'Overdue Tasks',
      value: health.overdueTasks,
      status: getStatus(health.overdueTasks, { warning: 3, critical: 10 })
    },
    {
      icon: CreditCard,
      label: 'Failed Payments',
      value: health.failedPayments,
      status: getStatus(health.failedPayments, { warning: 1, critical: 5 })
    },
    {
      icon: MessageSquare,
      label: 'Unread Messages',
      value: health.unreadMessages,
      status: getStatus(health.unreadMessages, { warning: 10, critical: 25 })
    }
  ];

  const healthScoreColor =
    health.healthScore >= 80 ? 'text-emerald-600' :
    health.healthScore >= 50 ? 'text-amber-500' : 'text-red-500';

  const healthScoreBg =
    health.healthScore >= 80 ? 'bg-emerald-50 border-emerald-100' :
    health.healthScore >= 50 ? 'bg-amber-50 border-amber-100' : 'bg-red-50 border-red-100';

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="p-5 bg-white border-stone-200/80">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-stone-50">
              <Activity className="w-4 h-4 text-gray-600" />
            </div>
            <h3 className="font-semibold text-gray-900 font-outfit">System Health</h3>
          </div>
          <div className={`px-3 py-1 rounded-lg border ${healthScoreBg}`}>
            <span className={`text-sm font-semibold ${healthScoreColor}`}>
              {health.healthScore}%
            </span>
          </div>
        </div>

        <div className="space-y-0">
          {metrics.map((metric, index) => (
            <HealthMetric key={index} {...metric} />
          ))}
        </div>
      </Card>
    </motion.div>
  );
}

export default SystemHealthPanel;
